// src/components/Dashboard/CurrentStatsCard.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, MapPin, Truck, ArrowRightCircle, Info } from 'lucide-react';
import { Typography } from '@mui/material'; // Assuming Material UI is installed
import { cn } from '../../lib/utils'; // Adjust path as needed
// mockCurrentStats is no longer needed as data comes from props
import Button from '../../components/ui/button'; // Import your custom Button

// Glassmorphism Styles (re-defined or imported from a central theme file)
// Changed bg-white/10 to bg-white/50 for better visibility as per user request
const glassmorphism = 'bg-white/50 backdrop-blur-md rounded-xl border border-white/10 shadow-lg';
// Changed greenTheme to text-black for better visibility as per user request
const darkText = 'text-black';

// Framer Motion variants
const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/**
 * CurrentStatsCard component displays key statistics and handles its own expand/collapse UI.
 * It receives assignedCount and totalSitesCount as props.
 */
const CurrentStatsCard = ({ assignedCount, totalSitesCount }) => {
    // State for Current Stats expansion - now managed internally
    const [isStatsExpanded, setIsStatsExpanded] = useState(false);

    return (
        <AnimatePresence mode="wait">
            {isStatsExpanded ? (
                <motion.div
                    key="expanded-stats"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                    className="p-2 flex flex-col items-center gap-2"
                    style={{
                        background: 'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
                        borderRadius: '12px',
                        boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10), 0px 5px 20.100000381469727px_0px_rgba(255,255,255,0.35) inset',
                        outline: '1px solid white',
                        backdropFilter: 'blur(30px)',
                        flex: '1',
                    }}
                >
                    {/* Header with Title and Collapse Button */}
                    <div className="flex justify-between items-center w-full mb-2">
                        <div className="justify-center text-black text-3xl font-bold font-['Figtree'] leading-10">Current Stats</div>
                        <button
                            onClick={() => setIsStatsExpanded(false)}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-50/50 shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px]"
                            aria-label="Collapse Current Stats"
                        >
                            <ArrowRightCircle className="w-6 h-6 text-black" />
                        </button>
                    </div>

                    {/* Stats Content */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col items-center">
                            {/* Changed icon color to black */}
                            <Users className={cn('w-6 h-6 mb-1', darkText)} />
                            {/* Changed text color to black */}
                            <Typography variant="body1" className={darkText}>
                                Currently Assigned
                            </Typography>
                            {/* Changed text color to black */}
                            <Typography variant="h5" className={cn('font-bold', darkText)}>
                                {assignedCount}
                            </Typography>
                        </div>
                        <div className="flex flex-col items-center">
                            {/* Changed icon color to black */}
                            <MapPin className={cn('w-6 h-6 mb-1', darkText)} />
                            {/* Changed text color to black */}
                            <Typography variant="body1" className={darkText}>
                                Sites
                            </Typography>
                            {/* Changed text color to black */}
                            <Typography variant="h5" className={cn('font-bold', darkText)}>
                                {totalSitesCount}
                            </Typography>
                        </div>
                    </div>
                    {/* Removed the "Track Live Rides" button */}
                </motion.div>
            ) : (
                <motion.button
                    key="collapsed-stats"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => setIsStatsExpanded(true)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-neutral-50/50 shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px]"
                    aria-label="Expand Current Stats"
                >
                    <Info className="w-6 h-6 text-black" />
                </motion.button>
            )}
        </AnimatePresence>
    );
};

export default CurrentStatsCard;
